import React, { useEffect } from "react";

import ErrorBoundary from "components/ErrorBoundary";
import { LoaderSmall } from "components/Loader";
import { AddCardModal } from "components/CardManagement";
import EmailRequiredModal from "components/GlobalModals/EmailRequiredModal";
import InsufficientPointsModal from "components/GlobalModals/InsufficientPointsModal";
import { useScreenTracking } from "hooks/useScreenTracking";
import {
  trackResumoCompraEvent,
  trackViewCartMarketplaceItems,
  trackAddShippingInfoMarketplaceItems,
} from "utils/gtm";

import { useMarketplaceCheckout } from "./hooks/useMarketplaceCheckout";
import CheckoutHeader from "./_components/CheckoutHeader";
import FinancialSummary from "./_components/FinancialSummary";
import CouponField from "./_components/CouponField";
import PointsSlider from "./_components/PointsSlider";
import PixSection from "./_components/PixSection";
import CardSection from "./_components/CardSection";
import FinalizeButton from "./_components/FinalizeButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Page, SectionLabel, CheckoutLayout } from "./styles";

export default function MarketplaceCheckout() {
  const checkout = useMarketplaceCheckout();
  const isDesktop = useMediaQuery("(min-width:1024px)");
  const { loading, dataCheckout } = checkout;

  useScreenTracking();

  useEffect(() => {
    if (loading) return;
    const items = dataCheckout?.cart?.items || [];
    trackViewCartMarketplaceItems(items);
    trackAddShippingInfoMarketplaceItems(
      items,
      dataCheckout?.cart?.shipping_by_brand,
    );
  }, [loading, dataCheckout]);

  if (loading) return <LoaderSmall />;

  const summary = dataCheckout?.summary || {};
  const { financialSummary } = checkout;
  const couponInfo = summary.coupon || null;
  const couponForSummary =
    couponInfo && financialSummary.discountMoney > 0
      ? {
          discountMoney: financialSummary.discountMoney,
          discountPoints: financialSummary.discountPoints,
          code: couponInfo.code || couponInfo.name || "",
        }
      : null;

  const summaryBlock = (
    <FinancialSummary
      displayMode={checkout.displayMode}
      subtotalMoney={financialSummary.subtotalMoney}
      subtotalPoints={financialSummary.subtotalPoints}
      coupon={couponForSummary}
      totalMoney={financialSummary.totalMoney}
      totalPoints={financialSummary.totalPoints}
    />
  );

  const finalizeBlock = (
    <FinalizeButton
      disabled={!checkout.canFinalize}
      loading={checkout.loadingCheckout}
      onClick={checkout.finalize}
    />
  );

  const paymentBlocks = (
    <>
      {checkout.couponsEnabled && (
        <>
          <SectionLabel>Forma de pagamento</SectionLabel>
          <CouponField
            value={checkout.coupon}
            setValue={checkout.setCoupon}
            applied={Boolean(checkout.couponInfo)}
            info={checkout.couponInfo}
            error={checkout.couponError}
            loading={checkout.loadingCoupon}
            onApply={checkout.applyCoupon}
            onRemove={checkout.removeCoupon}
          />
        </>
      )}
      {checkout.showPointsSlider && (
        <PointsSlider
          pontos={checkout.sliderPontos}
          reais={checkout.sliderReais}
          min={checkout.minPontosSlider}
          max={checkout.pontosDisponiveis}
          onChange={checkout.handlePontosPreview}
          onTrackChange={(p, r) => {
            checkout.handlePontosCommit(p);
            trackResumoCompraEvent({
              title: "ajustar-pontos",
              pontos: p,
              reais: r,
            });
          }}
        />
      )}
      {checkout.paymentFlags?.pix !== false && (
        <PixSection
          selected={checkout.paymentMethod === "pix"}
          onSelect={(v) => checkout.setPaymentMethod(v ? "pix" : null)}
          disabled={checkout.reaisRestante === 0}
        />
      )}
      {checkout.paymentFlags?.card !== false && (
        <CardSection
          cards={checkout.cards}
          selectedCardId={checkout.selectedCardId}
          onSelectCard={(id) => {
            checkout.setSelectedCardId(id);
            checkout.setPaymentMethod("card");
          }}
          onRemoveCard={checkout.cardMgmt.onClickDelCard}
          onAddCard={checkout.cardMgmt.openAddCardModal}
          installments={checkout.installments}
          installmentsSelected={checkout.installmentsSelected}
          onPickInstallment={checkout.setInstallmentsSelected}
          disabled={checkout.reaisRestante === 0}
        />
      )}
      <AddCardModal cardMgmt={checkout.cardMgmt} cards={checkout.cards} />
    </>
  );

  return (
    <ErrorBoundary>
      <div className="wl-page">
        <Page>
          <CheckoutHeader />
          {isDesktop ? (
            <CheckoutLayout>
              <div className="co-methods">{paymentBlocks}</div>
              <div className="co-summary">
                {summaryBlock}
                {finalizeBlock}
              </div>
            </CheckoutLayout>
          ) : (
            <>
              {summaryBlock}
              {paymentBlocks}
              {finalizeBlock}
            </>
          )}
          <EmailRequiredModal
            show={checkout.showEmailRequired}
            onClose={() => checkout.setShowEmailRequired(false)}
            onRegisterEmail={() => checkout.setShowEmailRequired(false)}
            onContinue={() => checkout.setShowEmailRequired(false)}
          />
          <InsufficientPointsModal
            show={checkout.showInsufficientPoints}
            onClose={() => checkout.setShowInsufficientPoints(false)}
            minPoints={Number(checkout.pontosValue || 0)}
            userBalance={checkout.userBalance}
          />
        </Page>
      </div>
    </ErrorBoundary>
  );
}
